import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const Sidebar = ({ isCollapsed = false, onToggleCollapse, restrictedMode = false }) => {
  const location = useLocation();
  const [isProfileOpen, setIsProfileOpen] = useState(false);

  const fullNavigationItems = [
    {
      section: 'Quotes',
      items: [
        { label: 'Quote Calculator', path: '/quote-calculator', icon: 'Calculator' },
        { label: 'Quote Management', path: '/quote-management', icon: 'FileText' },
      ]
    },
    {
      section: 'Clients',
      items: [
        { label: 'Client Database', path: '/client-database', icon: 'Users' },
        { label: 'Digital Review', path: '/digital-quote-review', icon: 'Eye' },
      ]
    },
    {
      section: 'Settings',
      items: [
        { label: 'Administrative Settings', path: '/administrative-settings', icon: 'Settings' },
      ]
    }
  ];

  const restrictedNavigationItems = [
    {
      section: 'Quotes',
      items: [
        { label: 'Quote Calculator', path: '/quote-calculator', icon: 'Calculator' },
      ]
    }
  ];

  // Use restricted navigation if in restricted mode (quote calculator)
  const navigationItems = restrictedMode ? restrictedNavigationItems : fullNavigationItems;

  const isActivePath = (path) => location?.pathname === path;

  const handleProfileToggle = () => {
    setIsProfileOpen(!isProfileOpen);
  };

  const handleLogout = () => {
    console.log('Logout clicked');
    setIsProfileOpen(false);
  };

  // Show restricted notice in quote calculator mode
  const showRestrictedNotice = restrictedMode && location?.pathname === '/quote-calculator';

  return (
    <>
      {/* Desktop Sidebar */}
      <aside className={`fixed left-0 top-0 z-40 h-screen bg-surface border-r border-border transition-all duration-200 ease-out ${
        isCollapsed ? 'w-16' : 'w-60'
      } hidden lg:block`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center h-16 px-4 border-b border-border">
            <Link to="/quote-calculator" className="flex items-center space-x-3">
              <div className="flex items-center justify-center w-8 h-8 bg-primary rounded-lg flex-shrink-0">
                <Icon name="Calculator" size={20} color="white" />
              </div>
              {!isCollapsed && (
                <div className="flex flex-col">
                  <span className="text-lg font-semibold text-text-primary">KitchenQuote</span>
                  <span className="text-xs text-text-secondary font-medium">Pro</span>
                </div>
              )}
            </Link>
          </div>

          {/* Restricted Mode Notice */}
          {showRestrictedNotice && !isCollapsed && (
            <div className="mx-4 mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start space-x-2">
                <Icon name="Info" size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs font-medium text-amber-800 mb-1">Mode Calculateur</p>
                  <p className="text-xs text-amber-700">
                    Accès limité au calculateur de devis uniquement.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-6 overflow-y-auto">
            {navigationItems?.map((section) => (
              <div key={section?.section}>
                {!isCollapsed && (
                  <h3 className="px-2 mb-2 text-xs font-semibold text-text-secondary uppercase tracking-wider">
                    {section?.section}
                  </h3>
                )}
                <div className="space-y-1">
                  {section?.items?.map((item) => (
                    <Link
                      key={item?.path}
                      to={item?.path}
                      className={`flex items-center space-x-3 px-3 py-2 rounded-md text-sm font-medium transition-smooth group ${
                        isActivePath(item?.path)
                          ? 'bg-primary text-primary-foreground'
                          : 'text-text-secondary hover:text-text-primary hover:bg-muted'
                      }`}
                      title={isCollapsed ? item?.label : ''}
                    >
                      <Icon name={item?.icon} size={18} className="flex-shrink-0" />
                      {!isCollapsed && <span>{item?.label}</span>}
                    </Link>
                  ))}
                </div>
              </div>
            ))}

            {/* Restricted Access Notice for non-collapsed sidebar */}
            {restrictedMode && !isCollapsed && (
              <div className="mt-8 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <div className="flex items-center space-x-2 mb-2">
                  <Icon name="Lock" size={16} className="text-gray-600" />
                  <span className="text-sm font-medium text-gray-700">Accès restreint</span>
                </div>
                <p className="text-xs text-gray-600">
                  Pour accéder aux autres fonctionnalités, connectez-vous en tant qu'administrateur.
                </p>
              </div>
            )}
          </nav>

          {/* User Profile */}
          <div className="p-4 border-t border-border">
            <div className="relative">
              <Button
                variant="ghost"
                onClick={handleProfileToggle}
                className={`w-full justify-start text-text-secondary hover:text-text-primary hover:bg-muted ${
                  isCollapsed ? 'px-2' : 'px-3'
                }`}
                title={isCollapsed ? (restrictedMode ? 'Mode Calculateur' : 'John Doe') : ''}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  restrictedMode ? 'bg-amber-500' : 'bg-secondary'
                }`}>
                  <Icon name={restrictedMode ? "Calculator" : "User"} size={16} color="white" />
                </div>
                {!isCollapsed && (
                  <>
                    <div className="flex-1 text-left ml-3">
                      <p className="text-sm font-medium text-text-primary">
                        {restrictedMode ? 'Mode Calculateur' : 'John Doe'}
                      </p>
                      <p className="text-xs text-text-secondary">
                        {restrictedMode ? 'Accès restreint' : 'Kitchen Pro'}
                      </p>
                    </div>
                    <Icon name="ChevronUp" size={16} />
                  </>
                )}
              </Button>

              {isProfileOpen && (
                <div className={`absolute bottom-full mb-1 bg-popover border border-border rounded-md shadow-soft-lg z-50 ${
                  isCollapsed ? 'left-0 w-48' : 'left-0 right-0'
                }`}>
                  <div className="px-3 py-2 border-b border-border">
                    <p className="text-sm font-medium text-text-primary">
                      {restrictedMode ? 'Mode Calculateur' : 'John Doe'}
                    </p>
                    <p className="text-xs text-text-secondary">
                      {restrictedMode ? 'Accès limité au calculateur' : 'john.doe@company.com'}
                    </p>
                  </div>
                  {restrictedMode ? (
                    <Link
                      to="/login"
                      className="flex items-center space-x-2 w-full px-3 py-2 text-sm text-text-secondary hover:text-text-primary hover:bg-muted transition-smooth rounded-b-md"
                    >
                      <Icon name="LogIn" size={16} />
                      <span>Connexion Admin</span>
                    </Link>
                  ) : (
                    <button
                      onClick={handleLogout}
                      className="flex items-center space-x-2 w-full px-3 py-2 text-sm text-text-secondary hover:text-text-primary hover:bg-muted transition-smooth rounded-b-md"
                    >
                      <Icon name="LogOut" size={16} />
                      <span>Logout</span>
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Collapse Toggle */}
          {onToggleCollapse && (
            <div className="p-4 border-t border-border">
              <Button
                variant="ghost"
                size="sm"
                onClick={onToggleCollapse}
                className="w-full justify-center text-text-secondary hover:text-text-primary"
              >
                <Icon name={isCollapsed ? "ChevronRight" : "ChevronLeft"} size={16} />
              </Button>
            </div>
          )}
        </div>
      </aside>
      {/* Mobile Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 z-40 bg-surface border-t border-border lg:hidden">
        <div className="flex items-center justify-around px-2 py-2">
          {navigationItems?.slice(0, 2)?.flatMap(section => section?.items)?.map((item) => (
            <Link
              key={item?.path}
              to={item?.path}
              className={`flex flex-col items-center space-y-1 px-3 py-2 rounded-md transition-smooth ${
                isActivePath(item?.path)
                  ? 'text-primary' :'text-text-secondary hover:text-text-primary'
              }`}
            >
              <Icon name={item?.icon} size={20} />
              <span className="text-xs font-medium">{item?.label?.split(' ')?.[0]}</span>
            </Link>
          ))}
          
          {/* More Menu for Mobile - only show if not in restricted mode */}
          {!restrictedMode && (
            <div className="relative">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleProfileToggle}
                className="flex flex-col items-center space-y-1 text-text-secondary hover:text-text-primary"
              >
                <Icon name="MoreHorizontal" size={20} />
                <span className="text-xs font-medium">More</span>
              </Button>

              {isProfileOpen && (
                <div className="absolute bottom-full right-0 mb-2 w-48 bg-popover border border-border rounded-md shadow-soft-lg">
                  {navigationItems?.slice(1)?.flatMap(section => section?.items)?.map((item) => (
                    <Link
                      key={item?.path}
                      to={item?.path}
                      onClick={() => setIsProfileOpen(false)}
                      className={`flex items-center space-x-2 px-3 py-2 text-sm hover:bg-muted transition-smooth first:rounded-t-md ${
                        isActivePath(item?.path)
                          ? 'bg-primary text-primary-foreground'
                          : 'text-text-secondary hover:text-text-primary'
                      }`}
                    >
                      <Icon name={item?.icon} size={16} />
                      <span>{item?.label}</span>
                    </Link>
                  ))}
                  <div className="border-t border-border">
                    <button
                      onClick={handleLogout}
                      className="flex items-center space-x-2 w-full px-3 py-2 text-sm text-text-secondary hover:text-text-primary hover:bg-muted transition-smooth rounded-b-md"
                    >
                      <Icon name="LogOut" size={16} />
                      <span>Logout</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Login button for restricted mode on mobile */}
          {restrictedMode && (
            <Link
              to="/login"
              className="flex flex-col items-center space-y-1 px-3 py-2 rounded-md text-text-secondary hover:text-text-primary transition-smooth"
            >
              <Icon name="LogIn" size={20} />
              <span className="text-xs font-medium">Admin</span>
            </Link>
          )}
        </div>
      </nav>
    </>
  );
};

export default Sidebar;